import { api, type InfoShareDetail } from '../api/client';
import { createAndPersistInfoShareReplyKeys } from '../crypto/infoShareReplyKeys';
import {
  MAX_RELATIVE_EXPIRY_DAYS,
  clampExpiresAtToMaxRelative,
  maxExtendDaysFromNow,
} from '../crypto/infoShareLimits';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { tInfoShareStatus } from '../i18n/status';
import { confirmRevokeShare } from './confirmDialog';
import { bindModalDismiss, createModalShell } from './modalDialogShell';

const DEFAULT_MAX_REPLY_FILES = 10;
const DEFAULT_MAX_REPLY_MB = 100;
const BYTES_PER_MB = 1024 * 1024;

export { DEFAULT_MAX_REPLY_FILES, DEFAULT_MAX_REPLY_MB, BYTES_PER_MB };

function formatManageExpires(detail: InfoShareDetail): string {
  const mode = detail.multiUseAccess ? t('info.owner.accessMulti') : t('info.owner.accessSingle');
  const date = new Date(detail.expiresAt).toLocaleString();
  return `${t('info.owner.manageExpiresLabel')} ${date} · ${mode}`;
}

export function syncReplyOptionsVisibility(form: HTMLFormElement): void {
  const allowGuestReply = (form.querySelector('input[name="allowGuestReply"]') as HTMLInputElement).checked;
  const replyOptions = form.querySelector('#reply-options') as HTMLDivElement;
  const replyAllowsFiles = (form.querySelector('input[name="replyAllowsFiles"]') as HTMLInputElement).checked;
  const fileLimits = form.querySelector('#reply-file-limits') as HTMLDivElement;

  replyOptions.hidden = !allowGuestReply;
  fileLimits.hidden = !allowGuestReply || !replyAllowsFiles;
}

export async function openInfoShareManageDrawer(
  shareId: string,
  onUpdated: () => void | Promise<void>,
): Promise<void> {
  let detail: InfoShareDetail;
  try {
    detail = await api.getInfoShare(shareId);
  } catch (cause) {
    console.error('Load info share failed', cause);
    return;
  }

  const maxReplyFiles = detail.maxReplyFiles ?? DEFAULT_MAX_REPLY_FILES;
  const maxReplyMb = detail.maxReplyTotalBytes
    ? Math.max(1, Math.round(detail.maxReplyTotalBytes / BYTES_PER_MB))
    : DEFAULT_MAX_REPLY_MB;
  const maxExtendDays = Math.min(
    MAX_RELATIVE_EXPIRY_DAYS,
    maxExtendDaysFromNow(new Date(detail.expiresAt)),
  );

  const shell = createModalShell({
    titleKey: 'info.owner.manageTitle',
    helpTopic: dialogHelpTopic('infoShareManage'),
    className: 'manage-dialog',
  });

  shell.body.innerHTML = `
    <p class="muted" id="manage-status"></p>
    <p class="muted" id="manage-expires"></p>
    <p class="muted" id="manage-access-mode"></p>
    <p class="muted" id="manage-password-mode"></p>
    <form class="form-grid" id="manage-form">
      <label class="expiry-option">
        <span data-i18n="info.owner.extendBy"></span>
        <input type="number" name="extendDays" min="0" max="${maxExtendDays}" value="0" ${detail.canEdit ? '' : 'disabled'} />
        <span data-i18n="info.owner.expiresDays"></span>
      </label>
      <label class="checkbox-row">
        <input type="checkbox" name="allowGuestReply" ${detail.allowGuestReply ? 'checked' : ''} ${detail.canEdit ? '' : 'disabled'} />
        <span data-i18n="info.owner.allowGuestReply"></span>
      </label>
      <div id="reply-options" class="reply-options" ${detail.allowGuestReply ? '' : 'hidden'}>
        <label class="checkbox-row">
          <input type="checkbox" name="replyAllowsFiles" ${detail.replyAllowsFiles ? 'checked' : ''} ${detail.canEdit ? '' : 'disabled'} />
          <span data-i18n="info.owner.replyAllowsFiles"></span>
        </label>
        <div id="reply-file-limits" class="reply-file-limits" ${detail.allowGuestReply && detail.replyAllowsFiles ? '' : 'hidden'}>
          <label class="expiry-option">
            <span data-i18n="info.owner.maxReplyFiles"></span>
            <input type="number" name="maxReplyFiles" min="1" max="1000" value="${maxReplyFiles}" ${detail.canEdit ? '' : 'disabled'} />
          </label>
          <label class="expiry-option">
            <span data-i18n="info.owner.maxReplyTotalMb"></span>
            <input type="number" name="maxReplyTotalMb" min="1" max="10240" value="${maxReplyMb}" ${detail.canEdit ? '' : 'disabled'} />
            <span data-i18n="info.owner.maxReplyMbUnit"></span>
          </label>
        </div>
      </div>
    </form>
    <p class="error-text" id="manage-error" hidden></p>
  `;

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'button ghost';
  closeButton.id = 'manage-close';
  closeButton.innerHTML = '<span data-i18n="info.owner.dialogClose"></span>';

  const revokeButton = document.createElement('button');
  revokeButton.type = 'button';
  revokeButton.className = 'button ghost';
  revokeButton.id = 'manage-revoke';
  if (!(detail.canEdit && (detail.multiUseAccess || detail.displayStatus !== 'Opened'))) {
    revokeButton.hidden = true;
  }
  revokeButton.innerHTML = '<span data-i18n="info.owner.revoke"></span>';

  const saveButton = document.createElement('button');
  saveButton.type = 'submit';
  saveButton.className = 'button primary';
  saveButton.setAttribute('form', 'manage-form');
  saveButton.disabled = !detail.canEdit;
  saveButton.innerHTML = '<span data-i18n="info.owner.manageSave"></span>';

  shell.actions.append(closeButton, revokeButton, saveButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const status = shell.body.querySelector('#manage-status') as HTMLParagraphElement;
  const expires = shell.body.querySelector('#manage-expires') as HTMLParagraphElement;
  const accessMode = shell.body.querySelector('#manage-access-mode') as HTMLParagraphElement;
  const passwordMode = shell.body.querySelector('#manage-password-mode') as HTMLParagraphElement;
  const form = shell.body.querySelector('#manage-form') as HTMLFormElement;
  const error = shell.body.querySelector('#manage-error') as HTMLParagraphElement;

  const refreshLocale = () => {
    const openedHint = (detail.displayStatus === 'Expired' || detail.displayStatus === 'Purged')
      ? ` · ${detail.wasOpened ? t('info.owner.wasOpened') : t('info.owner.wasNotOpened')}`
      : '';
    status.textContent = `${t('info.owner.manageStatusLabel')} ${tInfoShareStatus(detail.displayStatus)}${openedHint}`;
    expires.textContent = formatManageExpires(detail);
    accessMode.textContent = `${t('info.owner.manageAccessModeLabel')} ${
      detail.multiUseAccess ? t('info.owner.accessMulti') : t('info.owner.accessSingle')
    }`;
    passwordMode.textContent = `${t('info.owner.managePasswordModeLabel')} ${
      detail.passwordMode === 'None'
        ? t('info.owner.passwordModeNone')
        : detail.passwordMode === 'UserSet'
          ? t('info.owner.passwordModeUserSet')
          : detail.passwordMode === 'Sms'
            ? t('info.owner.passwordModeSms')
            : detail.passwordMode
    }`;
    if (!error.hidden && error.dataset.errorKey) {
      error.textContent = t(error.dataset.errorKey);
    }
  };

  refreshLocale();
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  form.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (target.name === 'allowGuestReply' || target.name === 'replyAllowsFiles') {
      syncReplyOptionsVisibility(form);
    }
  });

  closeButton.addEventListener('click', close);

  revokeButton.addEventListener('click', () => {
    void (async () => {
      if (!(await confirmRevokeShare())) {
        return;
      }

      try {
        await api.revokeInfoShare(shareId);
        await onUpdated();
        close();
      } catch (cause) {
        console.error('Revoke failed', cause);
        error.hidden = false;
        error.dataset.errorKey = 'status.error';
        error.textContent = t('status.error');
      }
    })();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      error.hidden = true;
      const data = new FormData(form);
      const extendDays = Number.parseInt(String(data.get('extendDays') ?? '0'), 10);
      const allowGuestReply = data.get('allowGuestReply') === 'on';
      const replyAllowsFiles = allowGuestReply && data.get('replyAllowsFiles') === 'on';

      const payload: Parameters<typeof api.patchInfoShare>[1] = {
        allowGuestReply,
        replyAllowsFiles,
      };

      if (allowGuestReply && replyAllowsFiles) {
        const maxReplyFilesValue = Number.parseInt(String(data.get('maxReplyFiles') ?? ''), 10);
        const maxReplyMbValue = Number.parseInt(String(data.get('maxReplyTotalMb') ?? ''), 10);
        if (Number.isFinite(maxReplyFilesValue) && maxReplyFilesValue > 0) {
          payload.maxReplyFiles = maxReplyFilesValue;
        }
        if (Number.isFinite(maxReplyMbValue) && maxReplyMbValue > 0) {
          payload.maxReplyTotalBytes = maxReplyMbValue * BYTES_PER_MB;
        }
      }

      if (Number.isFinite(extendDays) && extendDays > 0) {
        const currentExpiry = new Date(detail.expiresAt);
        const requested = new Date(
          currentExpiry.getTime() + extendDays * 24 * 60 * 60 * 1000,
        );
        payload.expiresAt = clampExpiresAtToMaxRelative(requested).toISOString();
      }

      if (allowGuestReply && !detail.allowGuestReply) {
        const session = await api.getSession();
        payload.replyEncryptionPublicKey = await createAndPersistInfoShareReplyKeys(shareId, session.userId);
      }

      try {
        await api.patchInfoShare(shareId, payload);
        await onUpdated();
        close();
      } catch (cause) {
        console.error('Patch info share failed', cause);
        error.hidden = false;
        error.dataset.errorKey = 'status.error';
        error.textContent = t('status.error');
      }
    })();
  });
}

export function canRevokeInfoShare(share: {
  displayStatus: string;
  multiUseAccess: boolean;
}): boolean {
  if (
    share.displayStatus === 'Revoked'
    || share.displayStatus === 'Purged'
    || share.displayStatus === 'Expired'
  ) {
    return false;
  }

  if (!share.multiUseAccess && share.displayStatus === 'Opened') {
    return false;
  }

  return true;
}

export function canManageInfoShare(share: {
  displayStatus: string;
  multiUseAccess: boolean;
}): boolean {
  if (share.displayStatus === 'Revoked' || share.displayStatus === 'Purged' || share.displayStatus === 'Expired') {
    return false;
  }

  if (!share.multiUseAccess && share.displayStatus === 'Opened') {
    return false;
  }

  return true;
}
